import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { CheckoutProvider, ExpressCheckoutElement, useCheckout } from '@stripe/react-stripe-js'
import { useTranslation } from 'react-i18next'
import { stripeCheckoutLink } from '../../redux/actions/myAccountAction'
import { getStripe, walletsPossible } from './stripeClient'
import './express-checkout-row.css'

export interface ExpressCheckoutRowProps {
  /** Stripe price id (or lookup key) for the plan this row buys. */
  priceId?: string
  /** Regional presentment currency, same value the plan button sends. */
  currency?: string
  couponCode?: string
  trialDays?: number
  /** Fired when the buyer authorizes in the wallet sheet — funnel parity with the button path. */
  onConfirmStart?: () => void
}

const WALLET_ERROR = 'offers:expressCheckout.walletError'

/**
 * The buttons themselves. Split out because `useCheckout` only exists inside CheckoutProvider.
 * `checkout.confirm` finishes the session Stripe already built server-side, then sends the buyer
 * to the session's return_url (/payment-successful) — the same landing page as hosted Checkout.
 */
const WalletButtons = ({
  onConfirmStart,
  onError,
  onUnavailable,
  onSettled,
}: {
  onConfirmStart?: () => void
  onError: (message: string) => void
  onUnavailable: () => void
  /** Fired as soon as the element reports anything at all — see the silence guard below. */
  onSettled: () => void
}) => {
  const { t } = useTranslation('offers')
  const checkout = useCheckout()

  return (
    <ExpressCheckoutElement
      options={{
        buttonHeight: 44,
        buttonType: { applePay: 'subscribe', googlePay: 'subscribe' },
        // No maxRows here: Stripe forbids pairing it with overflow 'never', and a rejected
        // layout makes the element hang — neither `ready` nor `loaderror` ever fires, so the
        // row can never learn to collapse and renders a bare "or" above the plan button.
        layout: { maxColumns: 2, overflow: 'never' },
      }}
      onReady={(event) => {
        onSettled()
        // No wallet is set up on this device — collapse the row rather than leaving a gap.
        if (!event.availablePaymentMethods) onUnavailable()
      }}
      onLoadError={() => {
        onSettled()
        onUnavailable()
      }}
      onConfirm={async (event) => {
        onConfirmStart?.()
        const result = await checkout.confirm({ expressCheckoutConfirmEvent: event })
        if (result.type === 'error') {
          onError(result.error.message || t(WALLET_ERROR))
        }
      }}
    />
  )
}

/**
 * Apple Pay / Google Pay buttons for a single plan, rendered above that plan's own button.
 *
 * This is deliberately additive: the wallet row is a shortcut, and every path it can't serve
 * simply doesn't render. It stays silent when there's no publishable key, when the browser can't
 * show a wallet, and when the server declines to open a wallet session — which it does for
 * anyone whose plan change is an in-place subscription swap, since no wallet can confirm one.
 * Card buyers keep going through hosted Checkout exactly as before.
 */
const ExpressCheckoutRow = ({
  priceId,
  currency,
  couponCode,
  trialDays,
  onConfirmStart,
}: ExpressCheckoutRowProps) => {
  const stripe = useMemo(() => getStripe(), [])
  const canShowWallets = useMemo(() => walletsPossible(), [])
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [hidden, setHidden] = useState(false)
  const [settled, setSettled] = useState(false)
  const [error, setError] = useState('')
  const { t } = useTranslation('offers')

  // One session per plan + billing choice. Changing the billing toggle or applying a coupon
  // re-opens a session so the wallet sheet always quotes what the button next to it charges.
  const sessionKey = [priceId, currency, couponCode, trialDays].join('|')

  useEffect(() => {
    if (!stripe || !canShowWallets || !priceId) return undefined

    let cancelled = false
    setClientSecret(null)
    setHidden(false)
    setSettled(false)
    setError('')

    // Referly affiliate, as every card-button checkout already sends: without it a wallet
    // purchase through an affiliate's link would earn them nothing.
    const affiliateId =
      typeof window !== 'undefined' && (window as any).affiliateId ? String((window as any).affiliateId) : undefined

    stripeCheckoutLink({
      priceId,
      uiMode: 'custom',
      ...(currency && currency !== 'USD' && { currency }),
      ...(couponCode && { couponCode }),
      ...(trialDays && { trialDays }),
      ...(affiliateId && { affiliateId }),
    })
      .then((res: any) => {
        if (cancelled) return
        // The action resolves with an error *message* on failure, and the server answers with an
        // empty body when this buyer has no wallet-payable session. Both mean: no row.
        const secret = res && typeof res === 'object' ? res.client_secret : null
        if (secret) setClientSecret(secret)
        else setHidden(true)
      })
      .catch(() => {
        if (!cancelled) setHidden(true)
      })

    return () => {
      cancelled = true
    }
  }, [stripe, canShowWallets, priceId, currency, couponCode, trialDays])

  const fetchClientSecret = useCallback(async () => clientSecret as string, [clientSecret])

  // Belt and braces against a silent element. A rejected config can leave the Element emitting
  // neither `ready` nor `loaderror`, and a row that never hears back would sit there showing an
  // "or" above the plan button forever. Once it has reported anything the timer is disarmed, so
  // a working wallet row is never collapsed by this.
  useEffect(() => {
    if (!clientSecret || settled || hidden) return undefined
    const timer = setTimeout(() => setHidden(true), 8000)
    return () => clearTimeout(timer)
  }, [clientSecret, settled, hidden])

  if (!stripe || !canShowWallets || !priceId || hidden || !clientSecret) return null

  return (
    <div className='express-checkout-row'>
      {/* Keyed so a new session gets a fresh provider — CheckoutProvider options are fixed once set. */}
      <CheckoutProvider key={sessionKey} stripe={stripe} options={{ fetchClientSecret }}>
        <WalletButtons
          onConfirmStart={onConfirmStart}
          onError={setError}
          onUnavailable={() => setHidden(true)}
          onSettled={() => setSettled(true)}
        />
      </CheckoutProvider>
      {error && <p className='express-checkout-error'>{error}</p>}
      <div className='express-checkout-divider'>
        <span>{t('expressCheckout.or')}</span>
      </div>
    </div>
  )
}

export default ExpressCheckoutRow
